import React, { useState, useRef, useEffect } from 'react';
import { useEditMode } from '../EditModeContext';

const EditableText = ({
  id,
  defaultValue = '',
  as: Component = 'div',
  className = '',
  multiline = false,
  placeholder = 'Click to edit...',
  serviceId = null,
  children,
  onSave = null // Custom save handler (overrides default updateContent)
}) => {
  const { isEditMode, updateContent, getContent } = useEditMode();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [localValue, setLocalValue] = useState('');
  const inputRef = useRef(null);
  const initializedRef = useRef(false);

  // Initialize value once on mount
  useEffect(() => {
    if (!initializedRef.current) {
      const content = getContent(id, defaultValue || children, serviceId);
      setValue(content);
      setLocalValue(content);
      initializedRef.current = true;
    }
  }, []);

  // For all D1-backed fields, wait for D1 data and update when it arrives
  useEffect(() => {
    const d1BackedFields = [
      // WelcomeArea fields
      'welcomearea_title',
      'welcomearea_subtitle',
      'welcomearea_category',
      'welcomearea_description',
      'welcomearea_cta1',
      'welcomearea_pricing',
      // DesignerText fields
      'designertext_subtitle',
      'designertext_title1',
      'designertext_title2',
      'designertext_description',
      // Comparison section static fields
      'comparison_title',
      'comparison_service1_title',
      'comparison_service1_subtitle1',
      'comparison_service1_subtitle2',
      'comparison_service1_pricing',
      'comparison_service1_fineprint',
      'comparison_service1_learn_more_link',
      'comparison_service2_title',
      'comparison_service2_subtitle1',
      'comparison_service2_subtitle2',
      'comparison_service2_pricing',
      'comparison_service2_fineprint',
      'comparison_service2_learn_more_link',
      'comparison_service3_title',
      'comparison_service3_subtitle1',
      'comparison_service3_subtitle2',
      'comparison_service3_pricing',
      'comparison_service3_fineprint',
      'comparison_service3_learn_more_link'
    ];

    if (d1BackedFields.includes(id) && serviceId) {
      const content = getContent(id, defaultValue || children, serviceId);
      setValue(content);
      setLocalValue(content);
    }
  }, [id, serviceId, defaultValue, children, getContent]);

  // Update value when switching modes or serviceId changes, but NOT while editing
  useEffect(() => {
    if (!isEditing) {
      const content = getContent(id, defaultValue || children, serviceId);
      setValue(content);
      setLocalValue(content);
    }
  }, [isEditMode, serviceId]);

  // Listen for content updates (only when not editing)
  useEffect(() => {
    const handleContentUpdate = () => {
      if (!isEditing) {
        const content = getContent(id, defaultValue || children, serviceId);
        setValue(content);
        setLocalValue(content);
      }
    };

    window.addEventListener('contentUpdated', handleContentUpdate);
    return () => window.removeEventListener('contentUpdated', handleContentUpdate);
  }, [isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (multiline) {
        inputRef.current.select();
      } else {
        inputRef.current.setSelectionRange(localValue.length, localValue.length);
      }
    }
  }, [isEditing]);

  const handleClick = (e) => {
    if (isEditMode && !isEditing) {
      e.preventDefault();
      e.stopPropagation();
      setLocalValue(value); // Copy current value to local editing state
      setIsEditing(true);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Update local state only
  };

  const handleBlur = () => {
    // Save to context when done editing
    setValue(localValue);
    if (onSave) {
      onSave(localValue); // Use custom save handler if provided
    } else {
      updateContent(id, localValue, serviceId); // Pass serviceId to EditModeContext
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      setValue(localValue);
      if (onSave) {
        onSave(localValue); // Use custom save handler if provided
      } else {
        updateContent(id, localValue, serviceId); // Pass serviceId to EditModeContext
      }
      setIsEditing(false);
    }
    if (e.key === 'Escape') {
      setLocalValue(value); // Revert to saved value
      setIsEditing(false);
    }
  };

  // In edit mode but not editing - show with edit indicator
  if (isEditMode && !isEditing) {
    return (
      <Component
        className={`${className} cursor-pointer relative group transition-all`}
        onClick={handleClick}
        style={{ outline: isEditMode ? '2px dashed transparent' : 'none' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.outline = '2px dashed #3b82f6';
          e.currentTarget.style.outlineOffset = '4px';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.outline = '2px dashed transparent';
        }}
      >
        {value || placeholder}
        <span className="absolute -top-6 left-0 bg-blue-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
          Click to edit
        </span>
      </Component>
    );
  }

  // In edit mode and actively editing
  if (isEditMode && isEditing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`${className} border-2 border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded px-2 py-1 w-full bg-white text-gray-900`}
          placeholder={placeholder}
          rows={4}
          style={{ color: '#111827' }}
        />
      );
    }

    return (
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`${className} border-2 border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded px-2 py-1 w-full bg-white text-gray-900`}
        placeholder={placeholder}
        style={{ color: '#111827' }}
      />
    );
  }

  // Not in edit mode - render normally with saved value
  return (
    <Component className={className}>
      {value || children || defaultValue}
    </Component>
  );
};

export default EditableText;
